import React, { useState } from 'react';
import './ContactUs.css';
import contactBg1 from './img/contact-bg1.png';

// Icons
import { BsFillTelephoneFill } from "react-icons/bs";
import { MdEmail } from "react-icons/md";

// Cloudflare Turnstile
import { Turnstile } from "@marsidev/react-turnstile";

const ContactUs = () => {

    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [showTurnstile, setShowTurnstile] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        website_subject: '',
        website_message: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e: any) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin",
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken;
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const submitForm = async (token: string) => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) return;

        try {
            const response = await fetch('/api/contact_fnn/', {
                method: 'POST',
                credentials: "same-origin",
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken
                },
                body: JSON.stringify({
                    ...formData,
                    turnstileToken: token
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.error === "Captcha verification failed") {
                    setTurnstileToken(null);
                    setShowTurnstile(false); // Hide and reset so user can retry
                }
                throw new Error(data.error || 'Submission failed');
            }

            setSubmitted(true);
            setError('');
            setShowTurnstile(false);
            setTurnstileToken(null)

            setTimeout(() => {
                setSubmitted(false);
                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    website_subject: '',
                    website_message: ''
                });
            }, 6000);

        } catch (err: any) {
            console.error('Error submitting the form:', err);
            setError(err.message || 'Something went wrong. Please try again.');
        }
    };

    // On submit click, validate the form then show Turnstile
    const handleSubmit = (e: any) => {
        e.preventDefault();
        setError('');

        // If the Turnstile challenge is already done (e.g. retry), submit directly
        if (turnstileToken) {
            submitForm(turnstileToken);
        } else {
            // Reveal the Turnstile widget — submitForm will fire via onSuccess
            setShowTurnstile(true);
        }
    };

    return (
        <div className="home-container">
            <div className="contact-hero-section">
                <img className="contact-hero-image" alt="hero background" src={contactBg1} />

                <div className="dark-overlay"></div>

                <div className="overall-contact-wrapper">
                    <div className="contact-plus-prompt">
                        <h1>Contact Us</h1>
                        <p>Want to get in contact? Fill out this form and we&apos;ll get back to you as soon as we can.</p>
                        <h2>HELIOS POMPANO INC</h2>
                    </div>

                    <div className="contact-location-card-stack">
                        <div className="location-card">
                            <h1>Headquarters</h1>
                            <p className='contact-address'>
                                747 SW 2nd Avenue <br />
                                Suite 169 <br />
                                Gainesville, FL 32601 <br />
                                United States of America
                            </p>
                            <p className='contact-phone'><BsFillTelephoneFill style={{ width: '1.3rem', height: '1.3rem' }} />+1 (954)-681-1770 </p>
                        </div>

                        <div className="location-card">
                            <h1>South Florida Office</h1>
                            <p className='contact-address'>
                                2226 N.Cypress Bend Dr.<br />
                                Suite 209 <br />
                                Pompano Beach, FL 33069 <br />
                                United States of America
                            </p>
                            <p className='contact-phone2'><BsFillTelephoneFill style={{ width: '1.3rem', height: '1.3rem', marginLeft: '0.4rem' }} />+1 (954)-681-1770 </p>
                            <p className='contact-email'><MdEmail style={{ width: '2rem', height: '2rem' }} />info@fireneuralnetwork.com</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="get-in-touch-wrapper">
                <h1 style={{ color: '#28AAE1', fontWeight: '700' }}>Get In Touch</h1>
                <div id="sib-container" className="sib-container--large sib-container--vertical">
                    {submitted ? (
                        <h2 className="success-message">Thank you for contacting us!</h2>
                    ) : (
                        <form id="sib-form" onSubmit={handleSubmit}>
                            {/* First Name */}
                            <div>
                                <div className="sib-input sib-form-block">
                                    <input
                                        className="sibs-input"
                                        maxLength={200}
                                        type="text"
                                        name="first_name"
                                        placeholder="First Name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Last Name */}
                            <div>
                                <div className="sib-input sib-form-block">
                                    <input
                                        className="sibs-input"
                                        maxLength={200}
                                        type="text"
                                        name="last_name"
                                        placeholder="Last Name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <div className="sib-input sib-form-block">
                                    <input
                                        className="sibs-input"
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="sib-input sib-form-block">
                                    <label>
                                        Subject<span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <select
                                        className="sibs-input sibs-select-input"
                                        name="website_subject"
                                        value={formData.website_subject}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Select One</option>
                                        <option value="General Inquiry/Introduction">General Inquiry/Introduction</option>
                                        <option value="Detector Deployment Inquiry">Detector Deployment Inquiry</option>
                                        <option value="Employment/Internship Inquiry">Employment/Internship Inquiry</option>
                                    </select>
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <div className="sib-input sib-form-block">
                                    <label>Message<span style={{ color: 'red' }}>*</span> </label>
                                    <textarea
                                        className="sibs-input"
                                        name="website_message"
                                        placeholder="Leave a Message"
                                        rows={4}
                                        style={{ resize: 'vertical' }}
                                        value={formData.website_message}
                                        onChange={handleChange}
                                        required
                                    ></textarea>
                                    <p style={{ marginTop: '0.5rem' }}><strong>Customize your message to help us understand your request.</strong></p>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div>
                                <button
                                    className="sibs-submit-button"
                                    type="submit"
                                >
                                    Submit
                                </button>
                            </div>

                            {/* Turnstile only mounts after Submit is clicked */}
                            {showTurnstile && (
                                <div className='turnstile-wrapper'>
                                    <Turnstile
                                        siteKey="0x4AAAAAACiHB4c31UPAbHHe"
                                        options={{
                                            theme: 'light',
                                            size: 'normal',
                                            language: 'auto',
                                        }}
                                        onSuccess={(token) => {
                                            setTurnstileToken(token);
                                            submitForm(token); // Auto-submit once challenge passes
                                        }}
                                    />
                                </div>
                            )}

                            {error && <p className="error-website_message">{error}</p>}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactUs;